<script setup lang="ts">
import { inject, toRefs, computed } from 'vue';
import ViewOverlayGrid from '@/src/components/ViewOverlayGrid.vue';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import { Maybe } from '@/src/types';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useWindowingConfig } from '@/src/composables/useWindowingConfig';
import { useOrientationLabels } from '@/src/composables/useOrientationLabels';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import WindowLevelControls from '@/src/components/tools/windowing/WindowLevelControls.vue';
import { useImage } from '@/src/composables/useCurrentImage';
// @ts-ignore
import { useDicomRawStore } from '@src/store/dicom';

import {
  useDICOMStore,
  OtherInfo,
  VolumeInfo,
} from '@/src/store/datasets-dicom';
import { vtkFieldRef } from '@/src/core/vtk/vtkFieldRef';
import { useElementSize } from '@vueuse/core';
import { useVTKMultiWorldToSVG } from '@/src/composables/useVTKWorldToDisplay';

const dicomStore = useDICOMStore();
const dicomRawStore = useDicomRawStore();

const NICE_SCALE_STEPS = [1, 2, 5];
const TARGET_SCALE_PX = 96;
const MIN_SCALE_PX = 56;
const MAX_SCALE_PX = 140;
const SCALE_BAR_EDGE_MARGIN = 18;
const SCALE_BAR_BOTTOM_OFFSET = 30;
const SCALE_BAR_TICK_MINOR = 4;
const SCALE_BAR_TICK_MAJOR = 8;
const SCALE_BAR_DIVISIONS = 10;
const SCALE_BAR_IMAGE_GAP = 18;
const SCALE_BAR_FALLBACK_BOTTOM = 112;

function chooseNiceScaleMm(
  mmPerPixel: number,
  availablePx: number
): number | null {
  if (!Number.isFinite(mmPerPixel) || mmPerPixel <= 0) return null;

  const maxPx = Math.max(MIN_SCALE_PX, Math.min(MAX_SCALE_PX, availablePx));
  const targetPx = Math.min(TARGET_SCALE_PX, maxPx);
  const targetMm = targetPx * mmPerPixel;
  if (!Number.isFinite(targetMm) || targetMm <= 0) return null;

  const exponent = Math.floor(Math.log10(targetMm));
  const candidates = new Set<number>();

  for (let pow = exponent - 1; pow <= exponent + 2; pow += 1) {
    const scale = 10 ** pow;
    NICE_SCALE_STEPS.forEach((step) => {
      candidates.add(step * scale);
    });
  }

  const sortedCandidates = [...candidates].sort((a, b) => a - b);
  const inRange = sortedCandidates.filter((candidateMm) => {
    const px = candidateMm / mmPerPixel;
    return px >= MIN_SCALE_PX && px <= maxPx;
  });

  const ranked = (inRange.length ? inRange : sortedCandidates).map(
    (candidateMm) => {
      const px = candidateMm / mmPerPixel;
      const maxOverflow = Math.max(0, px - maxPx) * 10;
      return {
        candidateMm,
        score: Math.abs(px - targetPx) + maxOverflow,
      };
    }
  );

  ranked.sort((a, b) => a.score - b.score);
  return ranked[0]?.candidateMm ?? null;
}

function formatScaleLabel(lengthMm: number) {
  if (lengthMm >= 10 && Number.isInteger(lengthMm / 10)) {
    return `${lengthMm / 10} cm`;
  }
  if (Number.isInteger(lengthMm)) {
    return `${lengthMm} mm`;
  }
  return `${lengthMm.toFixed(1)} mm`;
}

function splitScaleLabel(label: string) {
  const [value, unit = ''] = label.split(' ');
  return { value, unit };
}

interface Props {
  viewId: string;
  imageId: Maybe<string>;
}

const props = defineProps<Props>();
const { viewId, imageId } = toRefs(props);

const view = inject(VtkViewContext);
if (!view) throw new Error('No VtkView');

const otherInfo = computed<OtherInfo>(() => {
  const targetImageId = props.imageId;
  if (targetImageId) {
    return dicomStore.otherInfo[targetImageId];
  }

  return {};
});
const volumeInfo = computed<VolumeInfo | null>(() => {
  const targetImageId = props.imageId;
  if (targetImageId) {
    return dicomStore.volumeInfo[targetImageId];
  }

  return null;
});

const { left: leftLabel } = useOrientationLabels(view);

const {
  config: sliceConfig,
  slice,
  range: sliceRange,
} = useSliceConfig(viewId, imageId);
const {
  config: wlConfig,
  width: windowWidth,
  level: windowLevel,
} = useWindowingConfig(viewId, imageId);
const { metadata } = useImage(imageId);

const viewContainer = vtkFieldRef(view.renderWindowView, 'container');
const { width: containerWidth, height: containerHeight } =
  useElementSize(viewContainer);
const parallelScale = vtkFieldRef(
  view.renderer.getActiveCamera(),
  'parallelScale'
);
const imageWorldCorners = computed(() => {
  const bounds = metadata.value.worldBounds;
  if (!bounds || bounds.length !== 6) return null;

  const [xmin, xmax, ymin, ymax, zmin, zmax] = bounds;
  return [
    [xmin, ymin, zmin],
    [xmin, ymin, zmax],
    [xmin, ymax, zmin],
    [xmin, ymax, zmax],
    [xmax, ymin, zmin],
    [xmax, ymin, zmax],
    [xmax, ymax, zmin],
    [xmax, ymax, zmax],
  ];
});
const imageSvgCorners = useVTKMultiWorldToSVG(
  // @ts-ignore
  imageWorldCorners,
  computed(() => view.renderer)
);
const imageDisplayBounds = computed(() => {
  const corners = imageSvgCorners.value;
  if (!corners?.length) return null;

  const xs = corners.map(([x]) => x);
  const ys = corners.map(([, y]) => y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
});

const scaleBar = computed(() => {
  const width = containerWidth.value;
  const height = containerHeight.value;
  const scale = parallelScale.value;
  const displayBounds = imageDisplayBounds.value;

  if (!width || !height || !scale || !props.imageId) return null;
  if (width < 180 || height < 180) return null;

  const visibleHeightMm = scale * 2;
  const visibleWidthMm = visibleHeightMm * (width / height);
  const mmPerPixelX = visibleWidthMm / width;
  const mmPerPixelY = visibleHeightMm / height;
  const mmPerPixel = Math.max(mmPerPixelX, mmPerPixelY);

  const availablePx = Math.min(width * 0.22, height * 0.28);
  const lengthMm = chooseNiceScaleMm(mmPerPixel, availablePx);
  if (!lengthMm) return null;

  const lengthPx = lengthMm / mmPerPixel;
  const label = formatScaleLabel(lengthMm);
  const { value: labelValue, unit: labelUnit } = splitScaleLabel(label);
  const horizontalStartX = Math.round((width - lengthPx) / 2);
  const horizontalEndX = horizontalStartX + lengthPx;
  const horizontalY = displayBounds
    ? Math.min(
        Math.round(displayBounds.maxY + SCALE_BAR_IMAGE_GAP),
        height - SCALE_BAR_BOTTOM_OFFSET
      )
    : height - SCALE_BAR_FALLBACK_BOTTOM;
  const verticalX = displayBounds
    ? Math.min(
        Math.round(displayBounds.maxX + SCALE_BAR_IMAGE_GAP),
        width - SCALE_BAR_EDGE_MARGIN
      )
    : width - SCALE_BAR_EDGE_MARGIN;
  const verticalCenterY = displayBounds
    ? (displayBounds.minY + displayBounds.maxY) / 2
    : height / 2;
  const verticalStartY = Math.round(verticalCenterY - lengthPx / 2);
  const verticalEndY = verticalStartY + lengthPx;
  const tickStep = lengthPx / SCALE_BAR_DIVISIONS;

  const horizontalTicks = Array.from(
    { length: SCALE_BAR_DIVISIONS + 1 },
    (_, idx) => ({
      x: horizontalStartX + tickStep * idx,
      major: idx === 0 || idx === SCALE_BAR_DIVISIONS || idx === 5,
    })
  );

  const verticalTicks = Array.from(
    { length: SCALE_BAR_DIVISIONS + 1 },
    (_, idx) => ({
      y: verticalStartY + tickStep * idx,
      major: idx === 0 || idx === SCALE_BAR_DIVISIONS || idx === 5,
    })
  );

  return {
    width,
    height,
    lengthPx,
    horizontalStartX,
    horizontalEndX,
    horizontalY,
    horizontalLabelX: horizontalEndX + 8,
    horizontalLabelY: horizontalY - 8,
    verticalX,
    verticalStartY,
    verticalEndY,
    verticalLabelX: verticalX + 12,
    verticalLabelY: verticalEndY - 10,
    horizontalTicks,
    verticalTicks,
    labelValue,
    labelUnit,
  };
});
</script>

<template>
  <svg
    v-if="scaleBar"
    class="overlay-no-events scale-ruler-overlay"
    :viewBox="`0 0 ${scaleBar.width} ${scaleBar.height}`"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <g class="scale-ruler-shadow">
      <line
        :x1="scaleBar.horizontalStartX"
        :y1="scaleBar.horizontalY"
        :x2="scaleBar.horizontalEndX"
        :y2="scaleBar.horizontalY"
      />
      <line
        :x1="scaleBar.verticalX"
        :y1="scaleBar.verticalStartY"
        :x2="scaleBar.verticalX"
        :y2="scaleBar.verticalEndY"
      />
      <line
        v-for="tick in scaleBar.horizontalTicks"
        :key="`shadow-h-${tick.x}`"
        :x1="tick.x"
        :y1="scaleBar.horizontalY"
        :x2="tick.x"
        :y2="
          scaleBar.horizontalY -
          (tick.major ? SCALE_BAR_TICK_MAJOR : SCALE_BAR_TICK_MINOR)
        "
      />
      <line
        v-for="tick in scaleBar.verticalTicks"
        :key="`shadow-v-${tick.y}`"
        :x1="scaleBar.verticalX"
        :y1="tick.y"
        :x2="
          scaleBar.verticalX -
          (tick.major ? SCALE_BAR_TICK_MAJOR : SCALE_BAR_TICK_MINOR)
        "
        :y2="tick.y"
      />
    </g>
    <g class="scale-ruler-main">
      <line
        :x1="scaleBar.horizontalStartX"
        :y1="scaleBar.horizontalY"
        :x2="scaleBar.horizontalEndX"
        :y2="scaleBar.horizontalY"
      />
      <line
        :x1="scaleBar.verticalX"
        :y1="scaleBar.verticalStartY"
        :x2="scaleBar.verticalX"
        :y2="scaleBar.verticalEndY"
      />
      <line
        v-for="tick in scaleBar.horizontalTicks"
        :key="`main-h-${tick.x}`"
        :x1="tick.x"
        :y1="scaleBar.horizontalY"
        :x2="tick.x"
        :y2="
          scaleBar.horizontalY -
          (tick.major ? SCALE_BAR_TICK_MAJOR : SCALE_BAR_TICK_MINOR)
        "
      />
      <line
        v-for="tick in scaleBar.verticalTicks"
        :key="`main-v-${tick.y}`"
        :x1="scaleBar.verticalX"
        :y1="tick.y"
        :x2="
          scaleBar.verticalX -
          (tick.major ? SCALE_BAR_TICK_MAJOR : SCALE_BAR_TICK_MINOR)
        "
        :y2="tick.y"
      />
      <text
        :x="scaleBar.horizontalLabelX"
        :y="scaleBar.horizontalLabelY + 8"
        text-anchor="start"
      >
        <tspan :x="scaleBar.horizontalLabelX" dy="0">
          {{ scaleBar.labelValue }}cm
        </tspan>
        <!--   <tspan v-if="scaleBar.labelUnit" :x="scaleBar.horizontalLabelX" dy="11">
          {{ scaleBar.labelUnit }}
        </tspan> -->
      </text>
      <text
        :x="scaleBar.verticalLabelX - 10"
        :y="scaleBar.verticalLabelY + 30"
        text-anchor="start"
      >
        <tspan :x="scaleBar.verticalLabelX - 20" dy="0">
          {{ scaleBar.labelValue }} cm
        </tspan>
        <!-- <tspan v-if="scaleBar.labelUnit" :x="scaleBar.verticalLabelX" dy="11">
          {{ scaleBar.labelUnit }}
        </tspan> -->
      </text>
    </g>
  </svg>
  <view-overlay-grid class="overlay-no-events view-annotations">
    <template v-slot:top-left>
      <div class="annotation-cell">
        <span style="color: red">←点击还原</span>
      </div>
    </template>
    <!-- <template v-slot:top-center>
      <div class="annotation-cell">
        <span>{{ topLabel }}</span>
      </div>
    </template> -->
    <template v-slot:middle-left>
      <div class="annotation-cell">
        <span>{{ leftLabel }}</span>
      </div>
    </template>
    <template v-slot:bottom-left>
      <div class="annotation-cell">
        <div
          v-if="
            (otherInfo && otherInfo.PatientName) ||
            (dicomRawStore.userInfo && dicomRawStore.userInfo.patientName)
          "
        >
          {{ otherInfo.PatientName || dicomRawStore.userInfo.patientName }}
          {{ otherInfo.PatientSex || dicomRawStore.userInfo.gender }}
        </div>
        <div v-if="otherInfo && otherInfo.PatientAge">
          {{ otherInfo.PatientAge }}
        </div>
        <!--  <div v-if="otherInfo.AccessionNumber">
          {{ otherInfo.AccessionNumber }}
        </div> -->
        <div
          v-if="
            (otherInfo && otherInfo.HospitalName) ||
            (dicomRawStore.userInfo && dicomRawStore.userInfo.hospitalName)
          "
        >
          {{ otherInfo.HospitalName || dicomRawStore.userInfo.hospitalName }}
        </div>
        <!--  <div v-if="otherInfo.Part">
          {{ otherInfo.Part }}
        </div> -->
        <div v-if="otherInfo && otherInfo.SeriesDate">
          {{ otherInfo.SeriesDate }} {{ otherInfo.ContentTime || '' }}
        </div>
        <div v-if="sliceConfig">
          IM: {{ slice + 1 }}/{{ sliceRange[1] + 1 }}
        </div>
        <div v-if="wlConfig">
          W/L: {{ windowWidth.toFixed(2) }} / {{ windowLevel.toFixed(2) }}
        </div>
      </div>
    </template>
    <template v-slot:top-right
      ><div class="annotation-cell">
        <span>{{ metadata.name }} </span>
      </div>
      <div class="annotation-cell">
        <!-- <dicom-quick-info-button :image-id="imageId"></dicom-quick-info-button> -->
        <div v-if="otherInfo.ScanOptions">
          {{ otherInfo.ScanOptions }} {{ otherInfo.Modality || '' }}
        </div>
        <div v-if="otherInfo.Manufacturer">
          {{ otherInfo.Manufacturer || '' }}
        </div>
        <div v-if="otherInfo.ManufacturerModelName">
          {{ otherInfo.ManufacturerModelName || '' }}
          {{ otherInfo.XRayTubeCurrent || '' }}
        </div>
        <div v-if="otherInfo.KVP">
          {{ otherInfo.KVP || '' }}
          {{ otherInfo.Exposure || '' }}
        </div>

        <div v-if="imageId" class="overlay-window-level-controls">
          <v-menu
            no-click-animation
            :close-on-content-click="false"
            location="left"
          >
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                size="small"
                append-icon="mdi-menu-down"
              >
                调窗
              </v-btn>
            </template>

            <!--  <v-btn
              icon="mdi-circle-half-full"
              size="small"
              variant="text"
              @click="windowLevelMenuOpen = !windowLevelMenuOpen"
            >
              <v-tooltip activator="parent" location="left">
                Window / Level
              </v-tooltip>
            </v-btn>
 -->
            <div class="overlay-window-level-menu" @click.stop>
              <WindowLevelControls :view-id="viewId" :image-id="imageId" />
            </div>
          </v-menu>
        </div>
      </div>
    </template>
    <template #bottom-right>
      <div class="annotation-cell">
        <!--  <div v-if="volumeInfo && volumeInfo.Modality">
          {{ volumeInfo.Modality }}
        </div> -->
        <div v-if="dicomRawStore.code">
          {{ dicomRawStore.code }}
        </div>
        <div v-if="otherInfo && otherInfo.SpacingBetweenSlices">
          BS: {{ otherInfo.SpacingBetweenSlices }}mm
        </div>
        <div v-if="otherInfo && otherInfo.SliceThickness">
          ST: {{ otherInfo.SliceThickness }}mm
        </div>
        <div v-if="otherInfo && otherInfo.RepetitionTime">
          TR: {{ otherInfo.RepetitionTime }}
        </div>
        <div v-if="otherInfo && otherInfo.EchoTime">
          TE: {{ otherInfo.EchoTime }}
        </div>
        <div v-if="otherInfo && otherInfo.FieldOfView">
          Fov: {{ otherInfo.FieldOfView }}
        </div>

        <div
          v-if="
            !viewId.includes('-coronal') &&
            !viewId.includes('-sagittal') &&
            !viewId.includes('-axial') &&
            !viewId.includes('-multi-oblique') &&
            !/[dr|mr|dx]/gi.test(dicomRawStore.code)
          "
          class="annotation-cell"
          @click.stop
        >
          <ViewTypeSwitcher :view-id="viewId" :image-id="imageId" />
        </div>

        <!--  <div v-if="otherInfo.DeviceSerialNumber">
          {{ otherInfo.DeviceSerialNumber }}
        </div> -->
      </div>
    </template>
  </view-overlay-grid>
</template>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped>
.scale-ruler-overlay {
  overflow: visible;
}

.scale-ruler-shadow line {
  stroke: rgba(0, 0, 0, 0.9);
  stroke-width: 3;
  stroke-linecap: square;
}

.scale-ruler-main line {
  stroke: #15c86d;
  stroke-width: 1.2;
  stroke-linecap: square;
}

.scale-ruler-main text {
  fill: #15c86d;
  stroke: rgba(0, 0, 0, 0.9);
  stroke-width: 0.55;
  paint-order: stroke fill;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.overlay-window-level-controls {
  display: inline-block;
  margin-top: 8px;
  pointer-events: all;
  text-align: left;
  text-shadow: none;
  letter-spacing: normal;
  white-space: normal;
  font-size: 14px;
}

.overlay-window-level-menu {
  pointer-events: all;
}
</style>
